import { Controller } from 'react-hook-form';
import {
  LiliTextInput,
  LiliDropdown,
  LiliMaskedInputV2,
  LiliInternationalPhoneInput,
  LiliCheckboxNew,
  LiliError,
  type LiliCheckboxNewProps,
  type LiliTextInputProps,
  type LiliDropdownProps,
  type ILiliMaskedInputV2Props,
  LiliRadioBox,
  type LiliRadioBoxProps,
} from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@Services/utils/utils.ts';
import type React from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import type { KeyFilterType } from 'primereact/keyfilter';
import type { RegionCode } from 'google-libphonenumber';
interface ControlledTextInputProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  title?: string;
  props?: Partial<LiliTextInputProps>;
  id: string;
  accessoryItemComponent?: React.ReactNode;
  readonly?: boolean;
  tabindex?: number;
  disabled?: boolean;
  filterType?: KeyFilterType;
  max?: number;
  isSelectMenuDisabled?: boolean;
  onPhoneValidationStatusChange?: (isValid: boolean) => void;
  onBlur?: () => void;
  shouldUpperCase?: boolean;
  isPublic?: boolean; // Controls session recording privacy: true = data-public (record), false = data-private (mask). Defaults to false for security.
}
interface ControlledPhoneProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  title?: string;
  props?: Partial<LiliTextInputProps>;
  id: string;
  accessoryItemComponent?: React.ReactNode;
  readonly?: boolean;
  tabindex?: number;
  disabled?: boolean;
  filterType?: KeyFilterType;
  max?: number;
  isSelectMenuDisabled?: boolean;
  onPhoneValidationStatusChange?: (isValid: boolean) => void;
  onBlur?: () => void;
  initialIsoCode?: RegionCode | null;
  onIsoCodeChange?: (isoCode: RegionCode) => void;
  filteredCountries?: RegionCode[];
}

export const ControlledTextInput = <T extends FieldValues>({
  control,
  name,
  title,
  id,
  props,
  accessoryItemComponent,
  readonly,
  tabindex,
  disabled,
  filterType,
  max,
  shouldUpperCase,
  isPublic,
}: ControlledTextInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliTextInput
          {...props}
          readOnly={readonly}
          value={value}
          onBlur={() => {
            if (typeof value === 'string') {
              onChange(normalizeInputSpacesOnBlur(value)); // if value is string, it will be trimmed
            } else {
              onChange(value); // if value is not string, it will be passed as is
            }
            onBlur();
          }}
          onChange={(val) =>
            onChange(normalizeInputSpacesOnChange(shouldUpperCase ? val.toUpperCase() : val))
          }
          id={id}
          title={title}
          errorMessage={error?.message}
          tabIndex={tabindex}
          filterType={filterType}
          disabled={disabled}
          maxLength={max}
          accessoryItemComponent={accessoryItemComponent}
          isPublic={isPublic}
          data-testid={id}
        />
      )}
    />
  );
};

interface ControlledDropdownProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  title: string;
  items: Array<{ title: string; value: string }>;
  id: string;
  props?: LiliDropdownProps;
  readonly?: boolean;
  tabIndex?: number;
  disabled?: boolean;
}

export const ControlledDropdown = <T extends FieldValues>({
  control,
  name,
  title,
  items,
  id,
  props,
  disabled,
  tabIndex,
}: ControlledDropdownProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
        return (
          <LiliDropdown
            {...props}
            value={items.find((item) => item.value === value?.value) || { title: '', value: '' }}
            onSelect={(item) => {
              onChange(item);
            }}
            onBlur={onBlur}
            onClear={() => onChange({ title: '', value: '' })}
            disabled={disabled}
            title={title}
            items={items}
            id={id}
            data-testid={id}
            errorMessage={error?.message}
            tabIndex={tabIndex}
          />
        );
      }}
    />
  );
};

interface ControlledCheckboxNewProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  id: string;
  props?: Partial<LiliCheckboxNewProps>;
  handleOnChange?: (value: boolean) => void;
}

export const ControlledCheckboxNew = <T extends FieldValues>({
  control,
  name,
  props,
  id,
  handleOnChange,
}: ControlledCheckboxNewProps<T>) => {
  return (
    <ControllerWrapper name={name} control={control}>
      {({ value, onChange, onBlur, error }) => (
        <div className='flex flex-col items-start w-full'>
          <LiliCheckboxNew
            {...props}
            id={id}
            checked={Boolean(value)}
            onBlur={onBlur}
            onCheckedChange={(isChecked) => {
              handleOnChange?.(isChecked);
              onChange(isChecked as T[Path<T>]);
            }}
            data-testid={id}
          />
          {error?.message && <LiliError errorMessage={error.message} data-testid={`${id}-error`} />}
        </div>
      )}
    </ControllerWrapper>
  );
};

type ControlledMaskedInputProps<T extends FieldValues> = ControlledTextInputProps<T> &
  Pick<ILiliMaskedInputV2Props, 'mask' | 'hint'>;

export const ControlledMaskedInput = <T extends FieldValues>({
  control,
  name,
  title,
  id,
  props,
  readonly,
  mask,
  hint,
  isPublic,
  accessoryItemComponent,
  tabindex,
}: ControlledMaskedInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliMaskedInputV2
          {...props}
          readOnly={readonly}
          value={value}
          id={id}
          errorMessage={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          title={title}
          mask={mask}
          hint={hint}
          isPublic={isPublic}
          accessoryItemComponent={accessoryItemComponent}
          tabIndex={tabindex}
          data-testid={id}
        />
      )}
    />
  );
};

interface ControlledLiliRadioBoxProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  id: string;
  title: string;
  value: string | number;
  props?: Partial<
    Omit<LiliRadioBoxProps, 'title' | 'value' | 'name' | 'id' | 'checked' | 'onClick'>
  >;
  handleOnChange?: (value: string | number) => void;
}

export const ControlledLiliRadioBox = <T extends FieldValues>({
  control,
  name,
  props,
  id,
  title,
  value: radioValue,
  handleOnChange,
}: ControlledLiliRadioBoxProps<T>) => {
  return (
    <ControllerWrapper name={name} control={control}>
      {({ value, onChange }) => (
        <LiliRadioBox
          id={id}
          name={name}
          title={title}
          value={radioValue}
          checked={value === radioValue}
          onClick={(val) => {
            onChange(val as T[Path<T>]);
            handleOnChange?.(val);
          }}
          {...props}
        />
      )}
    </ControllerWrapper>
  );
};

export const ControlledPhoneInput = <T extends FieldValues>({
  id,
  name,
  control,
  disabled,
  isSelectMenuDisabled,
  onPhoneValidationStatusChange,
  onBlur: onInputBlur,
  initialIsoCode,
  onIsoCodeChange,
  filteredCountries,
  tabindex,
}: ControlledPhoneProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliInternationalPhoneInput
          id={id}
          onIsoCodeChange={(isoCode) => onIsoCodeChange?.(isoCode)}
          onBlur={() => {
            onInputBlur?.();
            onBlur();
          }}
          inputValue={value}
          initialIsoCode={initialIsoCode}
          onInputValueChange={(val) => onChange(val)}
          isDisabled={disabled}
          onFieldValidationStatusChange={(isValid) => onPhoneValidationStatusChange?.(isValid)}
          isSelectMenuDisabled={isSelectMenuDisabled}
          errorMessage={error?.message}
          filteredCountries={filteredCountries}
          data-testid={id}
          tabIndex={tabindex}
        />
      )}
    />
  );
};
